import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Save, Trash2, Eye } from 'lucide-react'
import { Button } from '@trafiflow/shared-ui'
import { Button as HeroButton, Tooltip, Badge } from '@heroui/react'
import { StatusIndicator } from '../shared/StatusIndicator'
import type { Funnel } from '../../types'

interface FunnelDetailHeaderProps {
  funnel: Funnel
  saving: boolean
  onSave: () => void
  onDelete: () => void
}

export function FunnelDetailHeader({ funnel, saving, onSave, onDelete }: FunnelDetailHeaderProps) {
  const navigate = useNavigate()

  return (
    <div
      className="flex items-center justify-between px-6 py-4 border-b"
      style={{
        borderColor: 'var(--fn-border)',
        backgroundColor: 'var(--fn-bg-card)',
      }}
    >
      <div className="flex items-center gap-4">
        <Tooltip content="Back to funnels">
          <HeroButton
            isIconOnly
            variant="light"
            size="sm"
            onPress={() => navigate('/funnels')}
            aria-label="Back to funnels"
          >
            <ArrowLeft className="w-4 h-4 text-fn-text-sub" />
          </HeroButton>
        </Tooltip>

        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-fn-text">
            {funnel.name}
          </h1>
          <StatusIndicator isActive={funnel.isActive} size="sm" />
          <Badge
            variant="flat"
            size="sm"
            className="text-fn-text-sub"
            style={{
              backgroundColor: 'var(--fn-bg-muted)',
            }}
          >
            v{funnel.version}
          </Badge>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Tooltip content="Preview funnel">
          <HeroButton
            variant="light"
            size="sm"
            startContent={<Eye className="w-4 h-4" />}
            aria-label="Preview funnel"
          >
            Preview
          </HeroButton>
        </Tooltip>

        <Tooltip content="Delete funnel">
          <HeroButton
            color="danger"
            variant="light"
            size="sm"
            startContent={<Trash2 className="w-4 h-4" />}
            onPress={onDelete}
            aria-label="Delete funnel"
          >
            Delete
          </HeroButton>
        </Tooltip>

        <Button
          variant="primary"
          className="text-sm"
          onClick={onSave}
          disabled={saving}
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  )
}
